import type {
  AdminDashboard,
  ClientActivityQuery,
  CriticalAlertsQuery,
  EmployeeAnalytics,
  OperationsMetrics,
  OrganizationInsights,
  PaginatedClientActivity,
  PaginatedCriticalAlerts,
  PaginatedPendingActions,
  PaginatedScheduledOperations,
  PaginatedScheduledTransactions,
  PaginatedTransactions,
  PendingActionsQuery,
  ScheduledOperationsQuery,
  ScheduledTransactionsQuery,
  SuperAdminDashboard,
  TransactionsQuery,
} from '@/types/analytics';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Analytics Service
 * Handles all analytics-related API endpoints from /api/v1/analytics/*
 */

export const analyticsService = {
  /**
   * Get employee analytics (list of employees with stats)
   */
  async getEmployeeAnalytics(): Promise<EmployeeAnalytics> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<EmployeeAnalytics>('/api/v1/analytics/employees', {}, token);
  },

  /**
   * Get organization insights (metrics for dashboard cards)
   */
  async getOrganizationInsights(): Promise<OrganizationInsights> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OrganizationInsights>(
      '/api/v1/analytics/organization/insights',
      {},
      token
    );
  },

  // ===== SUPER ADMIN DASHBOARD =====

  /**
   * Get main super admin dashboard with all platform metrics
   */
  async getSuperAdminDashboard(): Promise<SuperAdminDashboard> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<SuperAdminDashboard>(
      '/api/v1/analytics/super-admin/dashboard',
      {},
      token
    );
  },

  /**
   * Get full client activity list (paginated)
   */
  async getClientActivity(query?: ClientActivityQuery): Promise<PaginatedClientActivity> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.search) searchParams.append('search', query.search);
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);

    const url = `/api/v1/analytics/super-admin/client-activity${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedClientActivity>(url, {}, token);
  },

  /**
   * Get scheduled operations list (paginated)
   */
  async getScheduledOperations(
    query?: ScheduledOperationsQuery
  ): Promise<PaginatedScheduledOperations> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.type) searchParams.append('type', query.type);
    if (query?.date) searchParams.append('date', query.date);
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);

    const url = `/api/v1/analytics/super-admin/scheduled-operations${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedScheduledOperations>(url, {}, token);
  },

  /**
   * Get critical alerts list (paginated)
   */
  async getCriticalAlerts(query?: CriticalAlertsQuery): Promise<PaginatedCriticalAlerts> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.category) searchParams.append('category', query.category);
    if (query?.priority) searchParams.append('priority', query.priority);
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);

    const url = `/api/v1/analytics/super-admin/critical-alerts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedCriticalAlerts>(url, {}, token);
  },

  // ===== ADMIN DASHBOARD =====

  /**
   * Get main admin dashboard with all metrics
   */
  async getAdminDashboard(): Promise<AdminDashboard> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AdminDashboard>('/api/v1/analytics/admin/dashboard', {}, token);
  },

  /**
   * Get full transactions history (paginated)
   */
  async getTransactions(query?: TransactionsQuery): Promise<PaginatedTransactions> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.status) searchParams.append('status', query.status);
    if (query?.type) searchParams.append('type', query.type);
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);

    const url = `/api/v1/analytics/admin/transactions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedTransactions>(url, {}, token);
  },

  /**
   * Get full pending actions list (paginated)
   */
  async getPendingActions(query?: PendingActionsQuery): Promise<PaginatedPendingActions> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.actionType) searchParams.append('actionType', query.actionType);
    if (query?.priority) searchParams.append('priority', query.priority);
    if (query?.orderDirection) searchParams.append('orderDirection', query.orderDirection);

    const url = `/api/v1/analytics/admin/pending-actions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedPendingActions>(url, {}, token);
  },

  // ===== OPERATIONS DASHBOARD =====

  /**
   * Get operations metrics for dashboard
   */
  async getOperationsMetrics(): Promise<OperationsMetrics> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OperationsMetrics>(
      '/api/v1/analytics/operations/metrics',
      {},
      token
    );
  },

  /**
   * Get scheduled transactions (paginated)
   */
  async getScheduledTransactions(
    query?: ScheduledTransactionsQuery
  ): Promise<PaginatedScheduledTransactions> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.append('page', query.page.toString());
    if (query?.limit) searchParams.append('limit', query.limit.toString());
    if (query?.status) searchParams.append('status', query.status);

    const url = `/api/v1/scheduled-transactions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    return authenticatedRequest<PaginatedScheduledTransactions>(url, {}, token);
  },

  /**
   * Cancel a scheduled transaction
   */
  async cancelScheduledTransaction(id: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(`/api/v1/scheduled-transactions/${id}`, { method: 'DELETE' }, token);
  },

  /**
   * Retry a failed transaction
   */
  async retryTransaction(id: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(`/api/v1/transactions/${id}/retry`, { method: 'POST' }, token);
  },
};
